package co.uk.sacursoftware.ssinventory.ui

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import co.uk.sacursoftware.ssinventory.data.model.GenerateReportRequest
import co.uk.sacursoftware.ssinventory.data.remote.SSInventoryApi
import co.uk.sacursoftware.ssinventory.data.repository.SSInventoryRepository
import co.uk.sacursoftware.ssinventory.util.DateTimeUtils
import kotlinx.datetime.LocalDate
import kotlinx.coroutines.launch

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun GenerateReportScreen(
    onBack: () -> Unit,
    onReportGenerated: () -> Unit,
) {
    val scope = rememberCoroutineScope()
    val snackbarHostState = remember { SnackbarHostState() }
    val repository = remember { SSInventoryRepository(SSInventoryApi(SSInventoryApi.createDefaultClient())) }
    
    val sundays = remember { DateTimeUtils.getRecentSundays(24) }
    val saturdays = remember { DateTimeUtils.getRecentSaturdays(24) }
    
    var startDate by remember { mutableStateOf<LocalDate?>(null) }
    var endDate by remember { mutableStateOf<LocalDate?>(null) }
    var isGenerating by remember { mutableStateOf(false) }

    var showStartPicker by remember { mutableStateOf(false) }
    var showEndPicker by remember { mutableStateOf(false) }

    Scaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = {
            TopAppBar(
                title = { Text("Generate Report") },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        BackArrowIcon()
                    }
                }
            )
        }
    ) { padding ->
        Column(modifier = Modifier.padding(padding).fillMaxSize().padding(16.dp)) {
            Text(
                "Select a custom range for your report. Reports are generated based on the selected start (Sunday) and end (Saturday) dates.",
                style = MaterialTheme.typography.bodyMedium
            )
            
            Spacer(modifier = Modifier.height(24.dp))
            
            // Start Date Selection
            OutlinedCard(
                onClick = { showStartPicker = true },
                modifier = Modifier.fillMaxWidth()
            ) {
                Row(
                    modifier = Modifier.padding(16.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Column(modifier = Modifier.weight(1f)) {
                        Text("Start Date (Sunday)", style = MaterialTheme.typography.labelMedium)
                        Text(
                            startDate?.let { DateTimeUtils.formatDisplayDate(it) } ?: "Select Sunday",
                            style = MaterialTheme.typography.titleMedium,
                            color = if (startDate == null) MaterialTheme.colorScheme.outline else MaterialTheme.colorScheme.onSurface
                        )
                    }
                    Text(">", style = MaterialTheme.typography.titleMedium)
                }
            }
            
            Spacer(modifier = Modifier.height(16.dp))
            
            // End Date Selection
            OutlinedCard(
                onClick = { showEndPicker = true },
                modifier = Modifier.fillMaxWidth()
            ) {
                Row(
                    modifier = Modifier.padding(16.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Column(modifier = Modifier.weight(1f)) {
                        Text("End Date (Saturday)", style = MaterialTheme.typography.labelMedium)
                        Text(
                            endDate?.let { DateTimeUtils.formatDisplayDate(it) } ?: "Select Saturday",
                            style = MaterialTheme.typography.titleMedium,
                            color = if (endDate == null) MaterialTheme.colorScheme.outline else MaterialTheme.colorScheme.onSurface
                        )
                    }
                    Text(">", style = MaterialTheme.typography.titleMedium)
                }
            }
            
            if (startDate != null && endDate != null && startDate!! > endDate!!) {
                Text(
                    "Error: Start date must be before or same as end date.",
                    color = MaterialTheme.colorScheme.error,
                    style = MaterialTheme.typography.bodySmall,
                    modifier = Modifier.padding(top = 8.dp)
                )
            }
            
            Spacer(modifier = Modifier.weight(1f))
            
            Button(
                onClick = {
                    val s = startDate ?: return@Button
                    val e = endDate ?: return@Button
                    isGenerating = true
                    scope.launch {
                        val request = GenerateReportRequest(
                            startDate = DateTimeUtils.formatDate(s),
                            endDate = DateTimeUtils.formatDate(e)
                        )
                        repository.generateReport(request).fold(
                            onSuccess = {
                                if (it.error == null) {
                                    onReportGenerated()
                                } else {
                                    snackbarHostState.showSnackbar("Error: ${it.error}")
                                    isGenerating = false
                                }
                            },
                            onFailure = {
                                snackbarHostState.showSnackbar("Network error: ${it.message}")
                                isGenerating = false
                            }
                        )
                    }
                },
                modifier = Modifier.fillMaxWidth().padding(vertical = 16.dp),
                enabled = startDate != null && endDate != null && startDate!! <= endDate!! && !isGenerating,
                shape = MaterialTheme.shapes.medium
            ) {
                if (isGenerating) {
                    BrandLoadingSpinner(Modifier.size(32.dp))
                } else {
                    Text("GENERATE REPORT")
                }
            }
        }
        
        // Picker Dialogs
        if (showStartPicker) {
            DatePickerDialog(
                title = "Select Start Sunday",
                options = sundays,
                onDismiss = { showStartPicker = false },
                onSelect = { startDate = it; showStartPicker = false }
            )
        }
        
        if (showEndPicker) {
            DatePickerDialog(
                title = "Select End Saturday",
                options = saturdays,
                onDismiss = { showEndPicker = false },
                onSelect = { endDate = it; showEndPicker = false }
            )
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun DatePickerDialog(
    title: String,
    options: List<LocalDate>,
    onDismiss: () -> Unit,
    onSelect: (LocalDate) -> Unit
) {
    AlertDialog(
        onDismissRequest = onDismiss,
        confirmButton = {
            TextButton(onClick = onDismiss) { Text("CANCEL") }
        },
        title = { Text(title) },
        text = {
            Box(modifier = Modifier.heightIn(max = 400.dp)) {
                LazyColumn {
                    items(options) { date ->
                        ListItem(
                            headlineContent = { Text(DateTimeUtils.formatDisplayDate(date)) },
                            modifier = Modifier.clickable { onSelect(date) }
                        )
                        HorizontalDivider(color = MaterialTheme.colorScheme.outline.copy(alpha = 0.1f))
                    }
                }
            }
        }
    )
}
